'use client'
import { zodResolver } from '@hookform/resolvers/zod'
import { isValidPhoneNumber } from 'libphonenumber-js'
import Image from 'next/image'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import Input from '@/components/ui/CustomInput'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { PhoneInput } from '@/components/ui/phone-input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Toaster } from '@/components/ui/toaster'
import { toast } from '@/components/ui/use-toast'
import { Genders, PatientSalutations, PatientSalutationsValues } from '@/lib/generated-enums'
import { Open_Sans } from 'next/font/google'
const openSans = Open_Sans({ subsets: ['latin'] })

// Define the validation schema using Zod
const schema = z.object({
  title: z.nativeEnum(PatientSalutations),
  name: z.string().min(1, 'Name is required'),
  lastname: z.string().min(1, 'Last Name is required'),
  email: z.string().min(1, 'Email is required').email('Invalid email address'),
  phone: z.string().min(1, 'Phone Number is required').refine(isValidPhoneNumber, { message: 'Invalid phone number' }),
  clinicname: z.string().min(1, 'Clinic Name is required'),
  gmc: z.string().min(1, 'GMC, GDC, GPHC, NMC Number is required'),
  vat_number: z.string().optional(),
  cdpin: z.string().optional(),
  medical_director_name: z.string().min(1, 'Medical Director Name is required'),
  website: z.string().url('Please enter a valid URL (e.g., https://example.com)'),
  address: z.object({
    first_line: z.string().min(1, 'Address first line is required'),
    second_line: z.string().optional(),
    city: z.string().min(1, 'City is required'),
    post_code: z.string().min(1, 'Post Code is required'),
    country: z.string().min(1, 'Country is required'),
    location: z.string().nullable().optional(),
  }),
  tele: z.string().optional(),
  gender: z.nativeEnum(Genders),
  bank: z
    .object({
      bank_name: z.string().optional(),
      bank_address: z.string().optional(),
      bank_town: z.string().optional(),
      bank_postcode: z.string().optional(),
      bank_account_number: z.string().optional(),
      bank_sort_code: z.string().optional(),
    })
    .optional(),
})

const Page = () => {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      title: 0,
      name: '',
      lastname: '',
      email: '',
      phone: '',
      clinicname: '',
      gmc: '',
      vat_number: '',
      cdpin: '',
      medical_director_name: '',
      website: '',
      address: {
        first_line: '',
        second_line: '',
        city: '',
        post_code: '',
        country: '',
        location: null,
      },
      tele: '',
      gender: 0,
      bank: {
        bank_name: '',
        bank_address: '',
        bank_town: '',
        bank_postcode: '',
        bank_account_number: '',
        bank_sort_code: '',
      },
    },
    mode: 'onChange',
  })
  console.log(form.formState.errors)

  const onSubmit = async (data: z.infer<typeof schema>) => {
    const salutationString = Object.values(PatientSalutationsValues)[data.title]
    // Transform data to match API expected structure
    const apiData = {
      salutation: salutationString,
      first_name: data.name,
      last_name: data.lastname,
      email: data.email,
      phone_number: data.phone,
      clinic_name: data.clinicname,
      registration_number: data.gmc,
      vat_number: data?.vat_number,
      medical_director_name: data.medical_director_name,
      website: data.website,
      cd_pin_number: data.cdpin,
      address: {
        first_line: data.address.first_line,
        second_line: data.address.second_line || '',
        city: data.address.city,
        post_code: data.address.post_code,
        country: data.address.country,
        location: data.address.location,
      },
      tele: data.tele,
      bank: {
        bank_name: data.bank?.bank_name,
        bank_address: data.bank?.bank_address,
        bank_town: data.bank?.bank_town,
        bank_postcode: data.bank?.bank_postcode,
        bank_account_number: data.bank?.bank_account_number,
        bank_sort_code: data.bank?.bank_sort_code,
      },
    }

    setIsSubmitting(true)

    try {
      const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/clinics/requests/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(apiData),
      })

      if (!res.ok) {
        const errorData = await res.json();

        const firstErrorMessage = (() => {
          if (errorData && typeof errorData === 'object') {
            const values = Object.values(errorData) as unknown[];

            if (Array.isArray(values[0]) && typeof values[0][0] === 'string') {
              return values[0][0];
            }
          }
          return 'Registration failed';
        })();

        throw new Error(firstErrorMessage);
      }

      // Show success toast
      toast({
        title: 'Registration Successful',
        description: 'Your clinic registration request has been submitted.',
        variant: 'success',
      })

      // Optional: Reset form or redirect
      form.reset()
    } catch (error) {
      // Handle network or other errors
      toast({
        title: 'Registration Error',
        description: error instanceof Error ? error.message : 'An unexpected error occurred',
        variant: 'error',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div
      className={` ${openSans.className}
        px-4 py-6
        sm:px-6
        md:px-4 md:py-7 max-w-4xl md:max-w-4xl lg:max-w-5xl xl:max-w-7xl mx-auto
        grid grid-cols-1 md:grid-cols-2 gap-6 pb-16
      `}
    >
      <div className='flex flex-col'>
        <h2 className='text-left sm:text-3xl lg:text-[40px] font-semibold my-2'>Register Your Clinic</h2>
        <span>
          <p className='mt-2 text-left text-lg px-'>
            Start your journey to better healthcare by creating your clinic account.
          </p>
        </span>

        <div className='mt-6 flex w-full justify-center'>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className={`mt-4 flex flex-col gap-4`}
            >
              <h3 className='text-left text-sm font-bold'>Personal Information</h3>
              <div className='flex gap-2'>
                <div className='w-full md:w-1/4'>
                  <FormField
                    control={form.control}
                    name='title'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className='sr-only'>Salutation</FormLabel>
                        <Select onValueChange={(value) => field.onChange(Number(value))} value={field.value.toString()}>
                          <FormControl>
                            <SelectTrigger
                              className={`
                                flex h-14 w-full cursor-pointer appearance-none
                                justify-between rounded-lg bg-primary-input-blue
                                px-8 text-base font-medium leading-tight
                                text-navyBlue
                                focus:outline-none
                              `}
                            >
                              <SelectValue placeholder='Salutation' />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {Object.keys(PatientSalutations)
                              .filter((key) => isNaN(Number(key)))
                              .map((key) => (
                                <SelectItem
                                  key={PatientSalutations[key as keyof typeof PatientSalutations]}
                                  value={PatientSalutations[key as keyof typeof PatientSalutations].toString()}
                                >
                                  {PatientSalutationsValues[PatientSalutations[key as keyof typeof PatientSalutations]]}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className='flex gap-2'>
                <FormField
                  control={form.control}
                  name='name'
                  render={({ field }) => (
                    <FormItem className='flex-1'>
                      <FormLabel className='sr-only'>Name</FormLabel>
                      <FormControl>
                        <Input placeholder='*First Name' {...field} className={`h-14 bg-primary-input-blue`} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name='lastname'
                  render={({ field }) => (
                    <FormItem className='flex-1'>
                      <FormLabel className='sr-only'>Last Name</FormLabel>
                      <FormControl>
                        <Input placeholder='*Last Name' {...field} className={`h-14 bg-primary-input-blue`} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name='phone'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className='sr-only'>Phone Number</FormLabel>
                    <FormControl>
                      <PhoneInput
                        defaultCountry='GB'
                        placeholder='*Enter Phone Number'
                        {...field}
                        className='z-40 h-14 w-full bg-primary-input-blue'
                        numberInputProps={{ className: 'h-14 bg-primary-input-blue w-full' }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <h3 className='text-left text-sm font-bold mt-8'>Profession Information</h3>
              <FormField
                control={form.control}
                name='clinicname'
                render={({ field }) => (
                  <FormItem className='flex-1'>
                    <FormLabel className='sr-only'>Clinic Name</FormLabel>
                    <FormControl>
                      <Input placeholder='*Enter Clinic Name' {...field} className={`h-14 bg-primary-input-blue`} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='gmc'
                render={({ field }) => (
                  <FormItem className='flex-1'>
                    <FormLabel className='sr-only'>GMC, GDC, GPHC, NMC Number</FormLabel>
                    <FormControl>
                      <Input
                        placeholder='*Enter GMC, GDC, GPHC, NMC Number'
                        {...field}
                        className={`h-14 bg-primary-input-blue`}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className='grid grid-cols-2 gap-2'>
                <FormField
                  control={form.control}
                  name='vat_number'
                  render={({ field }) => (
                    <FormItem className='flex-1'>
                      <FormLabel className='sr-only'>VAT Number</FormLabel>
                      <FormControl>
                        <Input
                          placeholder='Enter VAT Number'
                          {...field}
                          className={`h-14 bg-primary-input-blue`}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name='cdpin'
                  render={({ field }) => (
                    <FormItem className='flex-1'>
                      <FormLabel className='sr-only'>CD Pin</FormLabel>
                      <FormControl>
                        <Input placeholder='Enter CD Pin' {...field} className={`h-14 bg-primary-input-blue`} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name='medical_director_name'
                render={({ field }) => (
                  <FormItem className='flex-1'>
                    <FormLabel className='sr-only'>Medical Director Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder='*Enter Medical Director Name'
                        {...field}
                        className={`h-14 bg-primary-input-blue`}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className='grid grid-cols-2 gap-2'>
                <FormField
                  control={form.control}
                  name='email'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className='sr-only'>Email Address</FormLabel>
                      <FormControl>
                        <Input placeholder='*Enter Your Email' {...field} className={`h-14 bg-primary-input-blue`} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name='website'
                  render={({ field }) => (
                    <FormItem className='flex-1'>
                      <FormLabel className='sr-only'>Website</FormLabel>
                      <FormControl>
                        <Input placeholder='*Enter Website' {...field} className={`h-14 bg-primary-input-blue`} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name='address.first_line'
                render={({ field }) => (
                  <FormItem className='flex-1'>
                    <FormLabel className='sr-only'>Address First Line</FormLabel>
                    <FormControl>
                      <Input
                        placeholder='*Address 1 Line'
                        {...field}
                        className={`h-14 bg-primary-input-blue`}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='address.second_line'
                render={({ field }) => (
                  <FormItem className='flex-1'>
                    <FormLabel className='sr-only'>Address Second Line (Optional)</FormLabel>
                    <FormControl>
                      <Input
                        placeholder='Address 2 Line'
                        {...field}
                        className={`h-14 bg-primary-input-blue`}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className='grid grid-cols-2 gap-2'>
                <FormField
                  control={form.control}
                  name='address.city'
                  render={({ field }) => (
                    <FormItem className='flex-1'>
                      <FormLabel className='sr-only'>City</FormLabel>
                      <FormControl>
                        <Input placeholder='*City' {...field} className={`h-14 bg-primary-input-blue`} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name='address.country'
                  render={({ field }) => (
                    <FormItem className='flex-1'>
                      <FormLabel className='sr-only'>Country</FormLabel>
                      <FormControl>
                        <Input placeholder='*Choose Country' {...field} className={`h-14 bg-primary-input-blue`} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className='grid grid-cols-2 gap-2'>
                <FormField
                  control={form.control}
                  name='address.post_code'
                  render={({ field }) => (
                    <FormItem className='flex-1'>
                      <FormLabel className='sr-only'>Post Code</FormLabel>
                      <FormControl>
                        <Input placeholder='*Enter Post Code' {...field} className={`h-14 bg-primary-input-blue`} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name='tele'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className='sr-only'>Telephone Number</FormLabel>
                      <FormControl>
                        <PhoneInput
                          defaultCountry='GB'
                          placeholder='Telephone Number'
                          {...field}
                          className='z-40 h-14 w-full bg-primary-input-blue'
                          numberInputProps={{ className: 'h-14 bg-primary-input-blue w-full' }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <h3 className='text-left text-sm font-bold mt-8'>Bank Details & Payment Terms (Optional)</h3>
              <div className='flex flex-col gap-3'>
                <FormField
                  control={form.control}
                  name='bank.bank_name'
                  render={({ field }) => (
                    <FormItem className='flex-1'>
                      <FormLabel className='sr-only'>Bank Name</FormLabel>
                      <FormControl>
                        <Input
                          placeholder='Bank Name'
                          {...field}
                          className={`h-14 bg-primary-input-blue`}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className='grid grid-cols-2 gap-2'>
                  <FormField
                    control={form.control}
                    name='bank.bank_address'
                    render={({ field }) => (
                      <FormItem className='flex-1'>
                        <FormLabel className='sr-only'>Bank Address</FormLabel>
                        <FormControl>
                          <Input
                            placeholder='Bank Address'
                            {...field}
                            className={`h-14 bg-primary-input-blue`}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name='bank.bank_town'
                    render={({ field }) => (
                      <FormItem className='flex-1'>
                        <FormLabel className='sr-only'>Town</FormLabel>
                        <FormControl>
                          <Input
                            placeholder='Town'
                            {...field}
                            className={`h-14 bg-primary-input-blue`}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name='bank.bank_postcode'
                  render={({ field }) => (
                    <FormItem className='flex-1'>
                      <FormLabel className='sr-only'>Post Code</FormLabel>
                      <FormControl>
                        <Input
                          placeholder='Post Code'
                          {...field}
                          className={`h-14 bg-primary-input-blue`}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name='bank.bank_account_number'
                  render={({ field }) => (
                    <FormItem className='flex-1'>
                      <FormLabel className='sr-only'>Account No</FormLabel>
                      <FormControl>
                        <Input
                          placeholder='Account No'
                          {...field}
                          className={`h-14 bg-primary-input-blue`}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name='bank.bank_sort_code'
                  render={({ field }) => (
                    <FormItem className='flex-1'>
                      <FormLabel className='sr-only'>Sort Code</FormLabel>
                      <FormControl>
                        <Input
                          placeholder='Sort Code'
                          {...field}
                          className={`h-14 bg-primary-input-blue`}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className='mt-16 text-left'>
                <button
                  type='submit'
                  disabled={isSubmitting}
                  className={`
                    w-1/2 rounded-lg bg-[#F59405] py-3 text-center font-bold
                    text-white
                    disabled:cursor-not-allowed disabled:opacity-50
                    hover:bg-[#E58904]
                  `}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </form>
          </Form>
        </div>
      </div>
      <div className='mt-20 flex w-full justify-end text-lg font-semibold text-[#A1BADB]'>
        <div className='relative h-[427px] w-[500px]'>
          <Image src='/images/clinics/registerclinic.png' alt='reg' fill className='object-contain' />
        </div>
      </div>
      <Toaster />
    </div>
  )
}

export default Page